import { gql } from 'graphql-request';

import {
  baseApi,
  select,
  insert,
  update,
  remove,
  afterRemove,
  afterInsert,
  afterUpdate,
  buildPaginationClauses,
} from './base';

import {
  CompanyAutoCompleteType,
  autoCompleteType,
  companyType,
  companyContactType,
} from '../graphqlTypes';
import { use } from 'react';

const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Search/paginated companies list similar to sheets/projects
    searchCompanies: select(builder, 'company', companyType, false, {
      query: (params = {}) => {
        const search = typeof params.search === 'string' ? params.search : '';
        const hasSearch = search.trim().length > 0;
        const scopeRole = typeof params.scopeRole === 'string' ? params.scopeRole : undefined;
        const scopeCompanyId =
          typeof params.scopeCompanyId === 'number' || typeof params.scopeCompanyId === 'string'
            ? params.scopeCompanyId
            : undefined;

        const andConditions = [];

        if (hasSearch) {
          const orParts = [
            `{ name: { _ilike: "%${search}%" } }`,
            `{ state: { _ilike: "${search}" } }`,
            `{ type: { _ilike: "${search}" } }`,
          ];
          andConditions.push(`{ _or: [ ${orParts.join(', ')} ] }`);
        }

        // Architect scoping (match devel):
        // _or: [ {id=_eq companyId}, {clients.company_id=_eq companyId}, {parent_id=_eq companyId} ]
        if (scopeRole === 'architect' && typeof scopeCompanyId !== 'undefined') {
          const literal =
            typeof scopeCompanyId === 'number' ? scopeCompanyId : `"${scopeCompanyId}"`;
          const scopeOr = [
            `{ id: { _eq: ${literal} } }`,
            `{ clients: { company_id: { _eq: ${literal} } } }`,
            `{ parent_id: { _eq: ${literal} } }`,
          ];
          andConditions.push(`{ _or: [ ${scopeOr.join(', ')} ] }`);
        }

        const where = andConditions.length
          ? `where: { _and: [ ${andConditions.join(', ')} ] }`
          : '';

        // Order: name desc when searching, else updated_at desc
        const order = hasSearch ? 'order_by: { name: desc }' : 'order_by: { updated_at: desc }';

        const clauses = [where, order].filter(Boolean);

        // include pagination; use helper for defaults and normalization
        const [limitClause, offsetClause] = buildPaginationClauses({
          params,
          hasSearch,
        });
        clauses.push(limitClause);
        clauses.push(offsetClause);

        const finalClauses = clauses.join(', ');

        return {
          body: gql`
              query {
                company(${finalClauses}) ${companyType}
              }
            `,
        };
      },
    }),
    searchForCompanyWithType: select(builder, 'company', CompanyAutoCompleteType, false, {
      filter: (params) => {
        if (params.search) {
          return `{_and: {
          _or: [{type: { _eq: "${params.type}" }}, {id: { _eq: ${params.id} }}]
          name: {_ilike: "%${params.search}%"}
          }}`;
        }
        return `{_or: [{type: { _eq: "${params.type}" }}, {id: { _eq: ${params.id} }}]}`;
      },
      order: '{ name: asc }',
    }),
    getArchitects: select(builder, 'company', autoCompleteType, false, {
      where: '{ is_verified: { _eq: true }, type: { _eq: "architect" } }',
    }),
    getClients: select(builder, 'company', autoCompleteType, false, {
      where: '{ is_verified: { _eq: true }, type: { _eq: "client" } }',
    }),
    getCompaniesFilter: select(builder, 'company', companyType, false, {
      filter: (params) =>
        `{${params.payload.type ? `type: {_eq: "${params.payload.type}"},` : ''} 
          ${params.payload.name ? `name: {_ilike: "%${params.payload.name}%"},` : ''} 
          ${params.payload.is_verified ? `is_verified: {_eq: ${params.payload.is_verified}},` : ''} 
          ${params.payload.parent_id ? `parent_id: {_eq: ${params.payload.parent_id}},` : ''} 
          ${params.payload.more ? `${params.payload.more}` : ''}
         }`,
      order: `{updated_at: desc}`,
      limit: 500,
      cacheKey: (params) => params.cacheKey,
    }),

    searchForCompanies: select(builder, 'company', companyType, false, {
      filter: (params) => {
        let search = '';
        if (params.search) {
          search = `name: {_ilike: "%${params.search}%"}`;
        }

        let type = '';
        if (params.type) {
          type = `type: {_eq: "${params.type}"}`;
        }

        let query = '';
        if (search && type) {
          query = `{_and: {${search}, ${type}}}`;
        } else if (search) {
          query = `{${search}}`;
        } else if (type) {
          query = `{${type}}`;
        }
        return query || `{}`;
      },
      order: `{name: asc}`,
    }),
    getCompanyWithId: select(builder, 'company', companyType, true),
    updateCompany: update(builder, 'company', companyType),
    insertCompany: insert(builder, 'company', companyType),
    getCompanyContacts: select(builder, 'company_contact', companyContactType, false, {
      filter: (params) => {
        const conditions = [];

        if (params.company_id) {
          conditions.push(`company_id: {_eq: ${params.company_id}}`);
        }

        if (params.state) {
          conditions.push(`_or: [{state: {_eq: "${params.state}"}}, {state: {_eq: ""}}]`);
        }

        if (params.main !== undefined) {
          conditions.push(`main: {_eq: ${params.main}}`);
        }

        if (conditions.length > 0) {
          return `{${conditions.join(', ')}}`;
        }

        return `{}`;
      },
    }),
    removeCompanyContact: remove(
      builder,
      'company_contact',
      companyContactType,
      afterRemove('getCompanyContacts')
    ),
    insertCompanyContact: insert(
      builder,
      'company_contact',
      companyContactType,
      afterInsert('getCompanyContacts')
    ),
    updateCompanyContact: update(
      builder,
      'company_contact',
      companyContactType,
      afterUpdate('getCompanyContacts')
    ),
  }),
  overrideExisting: false,
});

export const {
  useSearchCompaniesQuery,
  useSearchForCompanyWithTypeQuery,
  useLazySearchForCompanyWithTypeQuery,
  useGetArchitectsQuery,
  useGetClientsQuery,
  useGetCompaniesFilterQuery,
  useSearchForCompaniesQuery,
  useGetCompanyWithIdQuery,
  useUpdateCompanyMutation,
  useInsertCompanyMutation,
  useGetCompanyContactsQuery,
  useLazyGetCompanyContactsQuery,
  useInsertCompanyContactMutation,
  useUpdateCompanyContactMutation,
  useRemoveCompanyContactMutation,
} = companyApi;
